import { useState, useEffect } from "react";
import axios from "axios";
import { Settings, Trash2, AlertTriangle, Package, ClipboardList, AlertOctagon, Save, Loader2, PackageX, RefreshCw, Wrench, ShieldAlert, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { isAdmin } = useAuth();
  const [showDialog, setShowDialog] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  
  // Receiving location state
  const [receivingItems, setReceivingItems] = useState([]);
  const [receivingCount, setReceivingCount] = useState(0);
  const [loadingReceiving, setLoadingReceiving] = useState(false);
  const [clearingReceiving, setClearingReceiving] = useState(false);
  
  // Products without EAN state
  const [noEanProducts, setNoEanProducts] = useState([]);
  const [noEanCount, setNoEanCount] = useState(0);
  const [loadingNoEan, setLoadingNoEan] = useState(false);
  const [deletingNoEan, setDeletingNoEan] = useState(false);
  
  // Duplicates state
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [mergingDuplicates, setMergingDuplicates] = useState(false);

  // Data health state
  const [dataHealth, setDataHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [runningHealthAction, setRunningHealthAction] = useState(null);

  const fetchDataHealth = async () => {
    setLoadingHealth(true);
    try {
      const response = await axios.get(`${API}/admin/data-health`);
      setDataHealth(response.data);
    } catch (error) {
      console.error("Error fetching data health:", error);
    } finally {
      setLoadingHealth(false);
    }
  };

  const runHealthAction = async (action, label) => {
    setRunningHealthAction(action);
    try {
      const response = await axios.post(`${API}/admin/data-health/${action}`);
      const data = response.data || {};
      let msg = `${label} klart`;
      if (typeof data.fixed === "number") msg += ` — ${data.fixed} rader rensade`;
      if (typeof data.merged === "number") msg += ` — ${data.merged} sammanslagna`;
      if (typeof data.deleted === "number") msg += ` — ${data.deleted} borttagna`;
      if (data.skipped) msg += `, ${data.skipped} hoppade över`;
      toast.success(msg);
      fetchDataHealth();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Kunde inte köra ${label}`);
    } finally {
      setRunningHealthAction(null);
    }
  };
  
  // Low stock settings state
  const [lowStockThreshold, setLowStockThreshold] = useState(2);
  const [lowStockActivityDays, setLowStockActivityDays] = useState(30);
  const [savingLowStock, setSavingLowStock] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  
  // Fetch products without EAN
  const fetchNoEanProducts = async () => {
    setLoadingNoEan(true);
    try {
      const response = await axios.get(`${API}/inventory/products-without-ean`);
      setNoEanProducts(response.data.items || []);
      setNoEanCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching products without EAN:", error);
    } finally {
      setLoadingNoEan(false);
    }
  };
  
  // Delete products without EAN
  const handleDeleteNoEan = async () => {
    setDeletingNoEan(true);
    try {
      const response = await axios.delete(`${API}/inventory/products-without-ean`);
      toast.success(response.data.message);
      setShowDialog(null);
      fetchNoEanProducts();
    } catch (error) {
      console.error("Error deleting products without EAN:", error);
      toast.error(error.response?.data?.detail || "Kunde inte radera produkter");
    } finally {
      setDeletingNoEan(false);
    }
  };
  
  // Fetch duplicates
  const fetchDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await axios.get(`${API}/inventory/duplicates`);
      setDuplicates(response.data.duplicates || []);
      setDuplicateCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching duplicates:", error);
    } finally {
      setLoadingDuplicates(false);
    }
  };
  
  // Merge duplicates
  const handleMergeDuplicates = async () => {
    setMergingDuplicates(true);
    try {
      const response = await axios.post(`${API}/inventory/duplicates/merge`);
      toast.success(response.data.message);
      setShowDialog(null);
      fetchDuplicates();
    } catch (error) {
      console.error("Error merging duplicates:", error);
      toast.error(error.response?.data?.detail || "Kunde inte slå samman dubbletter");
    } finally {
      setMergingDuplicates(false);
    }
  };
  
  // Fetch receiving location items
  const fetchReceivingItems = async () => {
    setLoadingReceiving(true);
    try {
      const response = await axios.get(`${API}/inventory/receiving-location/items`);
      setReceivingItems(response.data.items || []);
      setReceivingCount(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching receiving items:", error);
    } finally {
      setLoadingReceiving(false);
    }
  };
  
  // Clear receiving location
  const handleClearReceiving = async () => {
    setClearingReceiving(true);
    try {
      const response = await axios.delete(`${API}/inventory/receiving-location/clear`);
      toast.success(response.data.message);
      setShowDialog(null);
      fetchReceivingItems();
    } catch (error) {
      console.error("Error clearing receiving location:", error);
      toast.error(error.response?.data?.detail || "Kunde inte rensa");
    } finally {
      setClearingReceiving(false);
    }
  };
  
  // Normalize receiving locations (merge "Mottagen order" variants)
  const [normalizingLocations, setNormalizingLocations] = useState(false);
  
  const handleNormalizeLocations = async () => {
    setNormalizingLocations(true);
    try {
      const response = await axios.post(`${API}/inventory/cleanup/normalize-locations`);
      toast.success(response.data.message);
      fetchReceivingItems();
    } catch (error) {
      console.error("Error normalizing locations:", error);
      toast.error(error.response?.data?.detail || "Kunde inte normalisera lagerplatser");
    } finally {
      setNormalizingLocations(false);
    }
  };
  
  // Fetch current settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/low-stock`);
        setLowStockThreshold(response.data.low_stock_threshold);
        setLowStockActivityDays(response.data.low_stock_activity_days);
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
    fetchReceivingItems();
    fetchDuplicates();
    fetchNoEanProducts();
    fetchDataHealth();
  }, []);
  
  // Save low stock settings
  const handleSaveLowStockSettings = async () => {
    setSavingLowStock(true);
    try {
      await axios.put(`${API}/settings/low-stock`, {
        low_stock_threshold: lowStockThreshold,
        low_stock_activity_days: lowStockActivityDays
      });
      toast.success("Inställningar sparade");
    } catch (error) {
      toast.error("Kunde inte spara: " + (error.response?.data?.detail || error.message));
    } finally {
      setSavingLowStock(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/inventory" replace />;
  }

  const handleClearHistory = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API}/history/clear`, {
        params: { clear_type: 'inventory' }
      });
      toast.success(response.data.message || "Lagerrörelse nollställd");
      setShowDialog(null);
    } catch (error) {
      toast.error("Kunde inte nollställa: " + (error.response?.data?.detail || error.message));
    } finally {
      setClearing(false);
    }
  };

  const handleClearPicking = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API}/picking/picking-lists/clear-history`);
      toast.success(response.data.message || "Plockhistorik nollställd");
      setShowDialog(null);
    } catch (error) {
      toast.error("Kunde inte nollställa: " + (error.response?.data?.detail || error.message));
    } finally {
      setClearing(false);
    }
  };

  const handleClearStatistics = async () => {
    setClearing(true);
    try {
      const response = await axios.delete(`${API}/history/clear`, {
        params: { clear_type: 'statistics' }
      });
      toast.success(response.data.message || "Statistik nollställd");
      setShowDialog(null);
    } catch (error) {
      toast.error("Kunde inte nollställa: " + (error.response?.data?.detail || error.message));
    } finally {
      setClearing(false);
    }
  };

  const handleResetInventory = async () => {
    if (confirmText !== "NOLLSTÄLL") {
      toast.error("Skriv NOLLSTÄLL för att bekräfta");
      return;
    }
    
    setClearing(true);
    try {
      const response = await axios.delete(`${API}/inventory/reset-all`);
      toast.success(response.data.message || "Lagret har nollställts");
      setShowDialog(null);
      setConfirmText("");
    } catch (error) {
      toast.error("Kunde inte nollställa: " + (error.response?.data?.detail || error.message));
    } finally {
      setClearing(false);
    }
  };

  const settingsOptions = [
    {
      id: 'history',
      title: 'Nollställ lagerrörelse',
      description: 'Radera all historik för lagerrörelser (inskanningar, utskanningar, bokningar, inventeringar, flyttningar)',
      icon: Package,
      action: handleClearHistory,
      items: ['Alla inskanningar', 'Alla utskanningar', 'Alla bokningar och avbokningar', 'Alla inventeringar', 'Alla flyttningar'],
      danger: false,
    },
    {
      id: 'picking',
      title: 'Nollställ plockhistorik',
      description: 'Radera alla slutförda plocklistor (aktiva listor behålls)',
      icon: ClipboardList,
      action: handleClearPicking,
      items: ['Alla slutförda plocklistor raderas', 'Aktiva plocklistor behålls'],
      danger: false,
    },
    {
      id: 'statistics',
      title: 'Nollställ sålda produkter',
      description: 'Nollställ statistiken från dagens datum. Historik bevaras.',
      icon: BarChart3,
      action: handleClearStatistics,
      items: ['Statistiken räknas från dagens datum', 'Historikloggar bevaras', 'Kan ångras'],
      danger: false,
    },
  ];

  const activeOption = settingsOptions.find(opt => opt.id === showDialog);
  
  // Calculate total cleanup issues
  const totalCleanupIssues = noEanCount + duplicateCount + receivingCount;

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />

      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Settings className="h-6 w-6 text-primary" />
            <h1
              className="font-heading text-2xl font-bold uppercase tracking-tight"
              data-testid="settings-title"
            >
              Inställningar
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <div className="max-w-3xl mx-auto">
          
          <Tabs defaultValue="cleanup" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="cleanup" className="gap-2">
                <Wrench className="h-4 w-4" />
                Dataunderhåll
                {totalCleanupIssues > 0 && (
                  <Badge variant="destructive" className="ml-1 h-5 px-1.5 text-xs">{totalCleanupIssues}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <Settings className="h-4 w-4" />
                Inställningar
              </TabsTrigger>
              <TabsTrigger value="danger" className="gap-2">
                <ShieldAlert className="h-4 w-4" />
                Nollställning
              </TabsTrigger>
            </TabsList>

            {/* CLEANUP TAB */}
            <TabsContent value="cleanup" className="space-y-4">
              <p className="text-muted-foreground text-sm mb-4">
                Verktyg för att rensa upp data-problem i systemet.
              </p>

              {/* DATA HEALTH OVERVIEW */}
              <Card data-testid="settings-card-data-health" className="border-blue-500/20 bg-blue-500/5">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart3 className="h-4 w-4 text-blue-400" />
                    Data-hälsa
                    {dataHealth && dataHealth.total_issues > 0 && (
                      <Badge variant="destructive" className="ml-auto">{dataHealth.total_issues}</Badge>
                    )}
                    {dataHealth && dataHealth.total_issues === 0 && (
                      <Badge variant="outline" className="ml-auto border-green-500/40 text-green-400">OK</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Periodisk hälsokontroll — kör städ-jobben med ett klick när du ser problem.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingHealth || !dataHealth ? (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <HealthRow label="Smutsiga EAN" value={dataHealth.issues.dirty_ean} testid="dh-dirty-ean" />
                        <HealthRow label="Smutsiga artnr" value={dataHealth.issues.dirty_artikelnummer} testid="dh-dirty-artnr" />
                        <HealthRow label="EAN-dubbletter" value={dataHealth.issues.duplicate_eans} testid="dh-dup-eans" />
                        <HealthRow label="Föräldralösa reservationer" value={dataHealth.issues.orphan_reservations} testid="dh-orphans" />
                        <HealthRow label="Tomma inköpsordrar" value={dataHealth.issues.empty_purchase_orders} testid="dh-empty-po" />
                        <HealthRow label="Negativt lager" value={dataHealth.issues.negative_inventory} testid="dh-negative" />
                      </div>
                      <div className="flex flex-col gap-2 pt-1">
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => runHealthAction("cleanup-whitespace", "Rensa whitespace")}
                            disabled={!!runningHealthAction || (dataHealth.issues.dirty_ean + dataHealth.issues.dirty_artikelnummer) === 0}
                            data-testid="btn-cleanup-whitespace"
                          >
                            {runningHealthAction === "cleanup-whitespace" ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Wrench className="h-3 w-3 mr-2" />}
                            Rensa whitespace
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => runHealthAction("merge-duplicates", "Slå ihop EAN-dubbletter")}
                            disabled={!!runningHealthAction || dataHealth.issues.duplicate_eans === 0}
                            data-testid="btn-merge-dups"
                          >
                            {runningHealthAction === "merge-duplicates" ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Wrench className="h-3 w-3 mr-2" />}
                            Slå ihop EAN
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => runHealthAction("cleanup-orphans", "Rensa föräldralösa reservationer")}
                            disabled={!!runningHealthAction || dataHealth.issues.orphan_reservations === 0}
                            data-testid="btn-cleanup-orphans"
                          >
                            {runningHealthAction === "cleanup-orphans" ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <Wrench className="h-3 w-3 mr-2" />}
                            Rensa orphans
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={fetchDataHealth}
                            disabled={loadingHealth}
                            data-testid="btn-refresh-health"
                          >
                            <RefreshCw className="h-3 w-3 mr-2" />
                            Uppdatera
                          </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground text-center">
                          Senast kollat: {dataHealth.checked_at ? new Date(dataHealth.checked_at).toLocaleString("sv-SE") : "—"}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Products Without EAN */}
              <Card data-testid="settings-card-no-ean">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <AlertOctagon className="h-4 w-4 text-red-500" />
                    Produkter utan EAN
                    {noEanCount > 0 && (
                      <Badge variant="destructive" className="ml-auto">{noEanCount}</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Produkter som saknar EAN-kod och inte kan nås via UI
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingNoEan ? (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  ) : noEanCount === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2">Inga problem</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-muted/50 rounded p-2 max-h-32 overflow-y-auto text-xs">
                        {noEanProducts.map((item, i) => (
                          <div key={i} className="flex justify-between py-1 border-b border-border last:border-0">
                            <span className="truncate">{item.produktnamn || item.artikelnummer || 'Okänd'}</span>
                            <span className="text-muted-foreground ml-2">{item.antal} st</span>
                          </div>
                        ))}
                      </div>
                      <Button size="sm" variant="destructive" onClick={() => setShowDialog('noean')} className="w-full">
                        <Trash2 className="h-3 w-3 mr-2" />
                        Radera alla
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Phantom Duplicates */}
              <Card data-testid="settings-card-duplicates">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    Phantom-dubbletter
                    {duplicateCount > 0 && (
                      <Badge variant="destructive" className="ml-auto">{duplicateCount}</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Produkter med samma EAN men tom plats eller på "Mottagen order"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingDuplicates ? (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  ) : duplicateCount === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-2">Inga problem hittades</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-muted/50 rounded p-2 max-h-32 overflow-y-auto text-xs">
                        {duplicates.slice(0, 5).map((dup, i) => (
                          <div key={i} className="py-1 border-b border-border last:border-0">
                            <span className="font-mono">{dup.ean}</span>
                            <span className="text-muted-foreground ml-2">
                              ({dup.entry_count} poster
                              {dup.has_null_location && ", tom plats"}
                              {dup.has_mottagen_order && ", mottagen"})
                            </span>
                          </div>
                        ))}
                        {duplicates.length > 5 && <p className="text-muted-foreground">+{duplicates.length - 5} till...</p>}
                      </div>
                      <Button size="sm" onClick={() => setShowDialog('duplicates')} className="w-full">
                        <Package className="h-3 w-3 mr-2" />
                        Slå samman alla
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Receiving Location */}
              <Card data-testid="settings-card-receiving">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <PackageX className="h-4 w-4 text-amber-500" />
                    Mottagningslagerplats
                    {receivingCount > 0 && (
                      <Badge variant="destructive" className="ml-auto">{receivingCount}</Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Produkter på MOTTAGEN ORDER som inte flyttats
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingReceiving ? (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  ) : receivingCount === 0 ? (
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground text-center py-2">Inga produkter kvar</p>
                      <Button size="sm" variant="secondary" onClick={handleNormalizeLocations} disabled={normalizingLocations} className="w-full">
                        {normalizingLocations ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-2" />}
                        Normalisera lagerplatsnamn
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-muted/50 rounded p-2 max-h-32 overflow-y-auto text-xs">
                        {receivingItems.map((item, i) => (
                          <div key={i} className="flex justify-between py-1 border-b border-border last:border-0">
                            <span className="truncate">{item.produktnamn || item.artikelnummer || 'Okänd'}</span>
                            <span className="text-muted-foreground ml-2">{item.antal} st</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={handleNormalizeLocations} disabled={normalizingLocations} className="flex-1">
                          {normalizingLocations ? <Loader2 className="h-3 w-3 mr-2 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-2" />}
                          Normalisera
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => setShowDialog('receiving')} className="flex-1">
                          <Trash2 className="h-3 w-3 mr-2" />
                          Rensa
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* SETTINGS TAB */}
            <TabsContent value="settings" className="space-y-4">
              <p className="text-muted-foreground text-sm mb-4">
                Konfigurera systeminställningar.
              </p>
              
              {/* Low Stock Settings */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Inställningar för lågt lager
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Endast produkter med aktivitet (utleverans) inom den angivna tidsperioden inkluderas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {loadingSettings ? (
                    <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="threshold" className="text-xs">Tröskelvärde (antal)</Label>
                          <Input
                            id="threshold"
                            type="number"
                            min="0"
                            value={lowStockThreshold}
                            onChange={(e) => setLowStockThreshold(parseInt(e.target.value) || 0)}
                          />
                          <p className="text-xs text-muted-foreground">Produkter med ≤{lowStockThreshold} st visas</p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="activity" className="text-xs">Aktivitetsperiod (dagar)</Label>
                          <Input
                            id="activity"
                            type="number"
                            min="1"
                            value={lowStockActivityDays}
                            onChange={(e) => setLowStockActivityDays(parseInt(e.target.value) || 1)}
                          />
                          <p className="text-xs text-muted-foreground">Senaste {lowStockActivityDays} dagarnas aktivitet</p>
                        </div>
                      </div>
                      <Button onClick={handleSaveLowStockSettings} disabled={savingLowStock} className="w-full">
                        {savingLowStock ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                        Spara inställningar
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* DANGER ZONE TAB */}
            <TabsContent value="danger" className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-400 flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4" />
                  Varning: Dessa åtgärder kan inte ångras!
                </p>
              </div>

              <div className="grid gap-3">
                {settingsOptions.map((option) => (
                  <Card key={option.id} className="border-muted">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <option.icon className="h-4 w-4 text-muted-foreground" />
                        {option.title}
                      </CardTitle>
                      <CardDescription className="text-xs">{option.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowDialog(option.id)}
                        className="w-full"
                      >
                        <Trash2 className="h-3 w-3 mr-2" />
                        Nollställ
                      </Button>
                    </CardContent>
                  </Card>
                ))}

                {/* Reset Entire Inventory */}
                <Card className="border-red-500/30 bg-red-500/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base text-red-400">
                      <AlertOctagon className="h-4 w-4" />
                      Nollställ hela lagret
                    </CardTitle>
                    <CardDescription className="text-xs">
                      VARNING: Raderar ALLA produkter och all data permanent
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDialog('reset')}
                      className="w-full"
                    >
                      <Trash2 className="h-3 w-3 mr-2" />
                      Nollställ allt
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      {/* Standard Confirmation Dialog */}
      <Dialog open={!!showDialog && showDialog !== 'reset-inventory'} onOpenChange={() => setShowDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {activeOption?.title}?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-4">
                <p>
                  Detta kommer att <strong>permanent radera</strong>:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                  {activeOption?.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
                <p className="text-destructive font-medium">
                  Denna åtgärd kan inte ångras!
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(null)} disabled={clearing}>
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={activeOption?.action}
              disabled={clearing}
              data-testid={`confirm-clear-${showDialog}`}
            >
              {clearing ? "Nollställer..." : `Ja, ${activeOption?.title?.toLowerCase()}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Inventory Confirmation Dialog - Extra safety */}
      <Dialog open={showDialog === 'reset-inventory'} onOpenChange={(open) => {
        if (!open) {
          setShowDialog(null);
          setConfirmText("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertOctagon className="h-6 w-6" />
              VARNING: Nollställ hela lagret?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-4 pt-4">
                <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <p className="font-bold text-destructive mb-2">
                    Detta kommer att PERMANENT radera:
                  </p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    <li>Alla produkter i lagret</li>
                    <li>Alla lagerplatser och antal</li>
                    <li>Alla bokningar</li>
                    <li>All import/export-historik</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-sm font-medium text-amber-600">
                    Tips: Exportera lagret först om du vill ha en backup!
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium">
                    Skriv <span className="font-mono bg-muted px-2 py-1 rounded">NOLLSTÄLL</span> för att bekräfta:
                  </p>
                  <Input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="Skriv NOLLSTÄLL här"
                    className="font-mono"
                    data-testid="confirm-reset-input"
                  />
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowDialog(null);
              setConfirmText("");
            }} disabled={clearing}>
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetInventory}
              disabled={clearing || confirmText !== "NOLLSTÄLL"}
              data-testid="confirm-reset-inventory-btn"
            >
              {clearing ? "Nollställer..." : "Ja, nollställ hela lagret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Receiving Location Dialog */}
      <Dialog open={showDialog === 'receiving'} onOpenChange={(open) => !open && setShowDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageX className="h-5 w-5 text-amber-500" />
              Rensa mottagningslagerplats?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p>
                  Detta kommer att radera <strong>{receivingCount} produkt{receivingCount !== 1 ? 'er' : ''}</strong> från mottagningslagerplatsen.
                </p>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-sm text-amber-600">
                    Produkter som inte flyttats till en permanent lagerplats efter mottagning raderas permanent.
                  </p>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(null)} disabled={clearingReceiving}>
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearReceiving}
              disabled={clearingReceiving}
              data-testid="confirm-clear-receiving-btn"
            >
              {clearingReceiving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rensar...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Ja, rensa alla
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Duplicates Dialog */}
      <Dialog open={showDialog === 'duplicates'} onOpenChange={(open) => !open && setShowDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Slå samman phantom-dubbletter?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p>
                  Detta kommer att slå samman <strong>{duplicateCount} grupper</strong> av phantom-dubbletter.
                </p>
                <div className="bg-muted/50 border rounded-lg p-3">
                  <p className="text-sm font-medium mb-2">Vad händer:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Poster utan lagerplats slås samman med huvudposten</li>
                    <li>• Poster på "MOTTAGEN ORDER" flyttas till huvudposten</li>
                    <li>• Antal summeras korrekt</li>
                    <li>• Posten med mest data behålls</li>
                  </ul>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(null)} disabled={mergingDuplicates}>
              Avbryt
            </Button>
            <Button
              onClick={handleMergeDuplicates}
              disabled={mergingDuplicates}
              data-testid="confirm-merge-duplicates-btn"
            >
              {mergingDuplicates ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Slår samman...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  Ja, slå samman
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Products Without EAN Dialog */}
      <Dialog open={showDialog === 'noean'} onOpenChange={(open) => !open && setShowDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertOctagon className="h-5 w-5 text-red-500" />
              Radera produkter utan EAN?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p>
                  Detta kommer att permanent radera <strong>{noEanCount} produkt{noEanCount !== 1 ? 'er' : ''}</strong> utan EAN-kod.
                </p>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-400">
                    ⚠️ Denna åtgärd kan inte ångras. Produkterna kommer att raderas permanent från lagret.
                  </p>
                </div>
                {noEanProducts.length > 0 && (
                  <div className="bg-muted/50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <p className="text-xs text-muted-foreground mb-2">Produkter som kommer raderas:</p>
                    {noEanProducts.slice(0, 5).map((item, index) => (
                      <p key={index} className="text-sm">
                        • {item.produktnamn || item.artikelnummer || 'Okänd'} ({item.antal} st @ {item.lagerplats})
                      </p>
                    ))}
                    {noEanProducts.length > 5 && (
                      <p className="text-xs text-muted-foreground mt-1">...och {noEanProducts.length - 5} till</p>
                    )}
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDialog(null)} disabled={deletingNoEan}>
              Avbryt
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteNoEan}
              disabled={deletingNoEan}
              data-testid="confirm-delete-no-ean-btn"
            >
              {deletingNoEan ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Raderar...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Ja, radera alla
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function HealthRow({ label, value, testid }) {
  const ok = !value;
  return (
    <div
      className={`flex items-center justify-between rounded-md px-2 py-1.5 ${ok ? "bg-muted/30" : "bg-red-500/10 border border-red-500/20"}`}
      data-testid={testid}
    >
      <span className="text-muted-foreground truncate">{label}</span>
      <span className={`font-mono font-semibold ${ok ? "text-green-400" : "text-red-400"}`}>{value}</span>
    </div>
  );
}
